import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import QueryBuilderPreview, {
	type QueryBuilderPreviewProps,
} from '@databox/ember-integrations/lib/components/Datasets/QueryBuilderPreview/QueryBuilderPreview';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type CustomMetricSetupFormDataset from 'webapp/models-custom/custom-metric-setup-form/dataset';

interface Args {
	model: CustomMetricSetupFormDataset;
}

export default class CustomQueryFormDatasetsDatasetsComponent extends Component<Args> {
	reactElement: ReactIntegrations.Root = null;

	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('react') declare reactService: Service.React;

	@action
	handleDatasetSaved(error?: { data?: { title: string } }) {
		if (!error) {
			this.infolayerService.ok(
				'Your data has been saved successfully. Hold tight while we prepare the updated data for you!',
			);
		} else {
			this.infolayerService.error(
				error.data?.title ??
					"We couldn't prepare your dataset. Please try again, or try again later.",
			);
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderDatasetView();

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderDatasetView);
		window.addEventListener('resize', this.onResize);
	}

	@action
	teardownView(): void {
		this.reactService.unmount(this.reactElement);

		window.removeEventListener('resize', this.onResize);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}

	@action
	modelChanged(): void {
		this.renderDatasetView();
	}

	@action
	renderDatasetView() {
		const model = this.args.model;

		const preselectedColumns = model.preselectedColumns;

		const newProps: QueryBuilderPreviewProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			dataset: { data: model.data, schema: model.schema },
			getHotInstance: (hot: SomeDataObject) => {
				const queryFormElement = document.querySelector<HTMLElement>('.form--custom-query');
				const querySetupElHeight = queryFormElement?.scrollHeight ?? 0;
				const popupHeight = queryFormElement?.closest('.popup')?.scrollHeight;
				const windowHeight = window.innerHeight;
				const calculatedHeight =
					// 130 is the height of the header
					// 20 is the height of the footer
					// 20 is the padding
					(popupHeight ? popupHeight : windowHeight) - querySetupElHeight - 130 - 20 - 20;
				const MIN_HEIGHT = 250;

				hot.hotInstance?.updateSettings(
					{
						height: Math.max(calculatedHeight, MIN_HEIGHT),
					},
					false,
				);
			},
			columnPalettes: this.buildColumnPalettes(model.selected),
			// the builder loads only once a dataset is chosen, so the id is set by the time this renders
			spaceAccessId: this.args.model.sourceId!,
			userId: +this.currentUserService.id!,
			isWhitelabeled: this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			onDataRefresh: this.datasetEventsService.handleDataRefresh,
			selectedSourceOption: this.args.model.selectedSourceOption,
			isTypeIngest: this.args.model.isTypeIngest,
			preselectedColumns,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				QueryBuilderPreview(newProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	private onResize() {
		this.renderDatasetView();
	}

	private buildColumnPalettes(selected: {
		measure?: string | null;
		date?: string | null;
		dimensions?: (string | null)[];
	}): NonNullable<QueryBuilderPreviewProps['columnPalettes']> {
		const palettes: NonNullable<QueryBuilderPreviewProps['columnPalettes']> = {};

		if (selected.measure) {
			palettes[selected.measure] = 'green';
		}

		if (selected.date) {
			palettes[selected.date] = 'orange';
		}

		selected.dimensions?.forEach((dim) => {
			if (dim) {
				palettes[dim] = 'brand';
			}
		});

		return palettes;
	}
	<template>
		<div
			id="datasets-preview-table"
			{{didInsert this.setupView}}
			{{didUpdate this.modelChanged @model.selected}}
			{{didUpdate this.renderDatasetView @model.data}}
			{{willDestroy this.teardownView}}
		>
		</div>
	</template>
}
